import {
  ArrowLeft,
  ChevronRight,
  Clock,
  Droplet,
  // Scale,
  TimerReset,
  // Users,
  User,
  type LucideIcon,
} from "lucide-react";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { useBackNavigation } from "../../hooks/useBackNavigation";
import BottomNavBar from "../../components/BottomNavBar";
import FingerPrickLogSheet from "../../components/FingerPrickLogSheet";
import FingerPrickPastListSheet from "../../components/FingerPrickPastListSheet";
import MedicationAddSheet from "../../components/MedicationAddSheet";
import MedicationPastListSheet from "../../components/MedicationPastListSheet";
import DiabetesFocusSheet from "../../components/DiabetesFocusSheet";
import ManualExerciseLogCard from "../../components/ManualExerciseLogCard";
import ManualExerciseLogSheet from "../../components/ManualExerciseLogSheet";
// import CreateGroupModal from "../../components/CreateGroupModal";
// import FamilyGroupsSheet from "../../components/FamilyGroupsSheet";
import GoalsService from "../../services/GoalsService";
// import GluceraNavigationRoutes from "../../utils/navigationRoutes";
// import { useGroups } from "../../hooks/useFamily";
import welcomeBg from "../../assets/homebg.png";
import glucoseReadingsService from "../../services/GlucoseReadingsService";
import measureService from "../../services/MeasureService";
import medicationService, { type LocalMedication } from "../../services/MedicationService";
import manualExerciseService from "../../services/ManualExerciseService";
import type { FingerPrickSavePayload } from "../../components/FingerPrickLogSheet";
import { appendFingerPrickLogHistory } from "../../utils/fingerPrickHistory";
import { getTodayExerciseLogs } from "../../utils/exerciseLogHistory";
import {
  getLocalDateString,
  mapApiRecordToEntry,
} from "../../utils/manualExerciseUi";
import {
  buildAttentionItems,
  buildCalibrationItems,
  buildMeasureHistoryReadings,
  buildSingleMedicationCalibrationCard,
  // buildWeightActivitySubtitle,
  getPoweringItems,
  // getWeightPoweringItem,
  METFORMIN_CALIBRATION_PLACEHOLDER,
  type InputCalibrationItem,
} from "../../utils/measurePageMappers";
import {
  measureActionLabel,
  measureCardHeading,
  measureCardMeta,
  measureCardMetaWarning,
  measureCardTitle,
  measureCardValueSubhead,
  measureEyebrow,
  measureEyebrowWarning,
  measureFocusBody,
  measureFocusTitle,
  measurePageSubtitle,
  measurePageTitle,
  measurePrimaryButtonLabel,
  measureSectionTitle,
} from "../../utils/gluceraTypography";



type ActivityLogCard = {
  id: string;
  titleSans: string;
  valueItalic: string;
  /** Workout-style "Title — italic" vs Weight "Title italic" inline */
  useEmDash: boolean;
  subtitleCaps: string;
  Icon: LucideIcon;
  leftAction?: string;
  rightAction?: string;
  /** When false, hides LOG NOW / LOG PAST footer (e.g. weight is read-only from API) */
  showActions?: boolean;
  /** Tap card → navigate (shows chevron when set) */
  detailRoute?: string;
};

type PersonalizationRow = {
  id: string;
  title: string;
  subtitleCaps: string;
  Icon: LucideIcon;
};

const personalizationRows: readonly PersonalizationRow[] = [
  // {
  //   id: "notifications",
  //   title: "Notifications",
  //   subtitleCaps: "MEAL REMINDERS • WALK PROMPTS",
  //   Icon: Bell,
  // },
  // Moved to Home page (post-meal)
  // {
  //   id: "family-group",
  //   title: "Family group",
  //   subtitleCaps: "",
  //   Icon: Users,
  // },
  // {
  //   id: "data-export",
  //   title: "Data export",
  //   subtitleCaps: "DOWNLOAD ALL • CSV / JSON",
  //   Icon: FileDown,
  // },
];

/**
 * Measure + Needs attention + Inputs — Glucera theme tokens.
 * Route: `/glucera/custom-screen`
 */
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const CustomScreenPage = () => {
  const { goBack } = useBackNavigation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [fingerSheetOpen, setFingerSheetOpen] = useState(false);
  const [fingerSheetMode, setFingerSheetMode] = useState<"now" | "past">("now");
  const [fingerPastSheetOpen, setFingerPastSheetOpen] = useState(false);
  const [fingerHistoryVersion, setFingerHistoryVersion] = useState(0);
  const [medAddSheetOpen, setMedAddSheetOpen] = useState(false);
  const [medPastSheetOpen, setMedPastSheetOpen] = useState(false);
  const [medicationMarkingId, setMedicationMarkingId] = useState<string | null>(null);
  const [focusSheetOpen, setFocusSheetOpen] = useState(false);
  const [focusSaving, setFocusSaving] = useState(false);
  const [exerciseLogSheetOpen, setExerciseLogSheetOpen] = useState(false);
  // const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  // const [familyGroupsSheetOpen, setFamilyGroupsSheetOpen] = useState(false);

  // const { data: groupsData, isLoading: groupsLoading } = useGroups();

  /** Opens only from Finger prick → LOG NOW (now) / LOG PAST (past). */
  const openFingerSheet = (sheetMode: "now" | "past") => {
    setFingerSheetMode(sheetMode);
    setFingerSheetOpen(true);
  };

  const openMedicationAddSheet = () => {
    setMedAddSheetOpen(true);
  };

  const openMedicationPastSheet = () => {
    setMedPastSheetOpen(true);
  };

  const openExerciseLogSheet = () => {
    setExerciseLogSheetOpen(true);
  };

  const todayLocal = getLocalDateString();

  // ── Measure page (GET /food_scanner/measure/) ─────────────────────────
  const {
    data: measureData,
    isLoading: measureLoading,
    refetch: refetchMeasure,
  } = useQuery({
    queryKey: ["measurePage"],
    queryFn: () => measureService.getMeasurePage({ showNotification: false }),
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const {
    data: medications = [],
    isLoading: medicationsLoading,
    refetch: refetchMedications,
  } = useQuery({
    queryKey: ["medications", "measure-inputs"],
    queryFn: () => medicationService.getAllMedications(),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: glucoseData, refetch: refetchGlucose } = useQuery({
    queryKey: ["glucoseReadings"],
    queryFn: () => glucoseReadingsService.getReadings({ showNotification: false }),
    staleTime: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const {
    data: todayExercises = [],
    isLoading: exercisesLoading,
    refetch: refetchExercises,
  } = useQuery({
    queryKey: ["manualExercises", todayLocal],
    queryFn: async () => {
      try {
        const records = await manualExerciseService.getManualExercises(todayLocal);
        if (records.length > 0) {
          return records.map(mapApiRecordToEntry);
        }
      } catch {
        // fall back to local cache
      }
      return getTodayExerciseLogs(todayLocal);
    },
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  // Refetch on every mount (user navigates to Measure page from Profile etc.)
  useEffect(() => {
    refetchMeasure();
    refetchGlucose();
  }, [refetchMeasure, refetchGlucose]);

  const poweringItems = getPoweringItems(measureData);
  // const weightPowering = getWeightPoweringItem(poweringItems);

  const fingerPastReadings = useMemo(() => {
    const date =
      measureData?.date_local ??
      `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`;
    // Only show what the measure API returns — no local cache, no glucose-readings snapshot
    return buildMeasureHistoryReadings(
      measureData,
      date,
      [],       // empty: ignore local finger-prick log
      undefined, // ignore: glucose-readings snapshot
    );
  }, [measureData]);

  const attentionItems = buildAttentionItems(poweringItems);

  const apiCalibration = buildCalibrationItems(poweringItems);
  const calibrationItems: readonly InputCalibrationItem[] = (() => {
    const finger =
      apiCalibration.find((c) => c.id === "finger-prick") ?? {
        id: "finger-prick",
        kind: "finger-prick" as const,
        title: "Finger prick",
        value: "—",
        subtitleCaps: "NO READING YET",
        Icon: Droplet,
        accent: "default" as const,
        leftAction: "LOG NOW",
        rightAction: "History",
      };
    const eating =
      apiCalibration.find((c) => c.id === "eating-window") ?? {
        id: "eating-window",
        kind: "eating-window" as const,
        title: "Eating window",
        value: "—",
        subtitleCaps: "NOT SET",
        Icon: Clock,
        accent: "default" as const,
        showActions: false,
      };
    const liveMg = glucoseData?.fingerPrick?.trim();

    const fingerCard: InputCalibrationItem = liveMg
      ? {
          ...finger,
          kind: "finger-prick",
          title: "Glucose reading",
          value: liveMg.includes("mg") ? liveMg : `${liveMg} mg/dL`,
          leftAction: finger.leftAction ?? "LOG NOW",
          rightAction: finger.rightAction ?? "History",
          showActions: finger.showActions ?? true,
        }
      : {
          ...finger,
          kind: "finger-prick",
          title: "Glucose reading",
          leftAction: finger.leftAction ?? "LOG NOW",
          rightAction: finger.rightAction ?? "History",
          showActions: finger.showActions ?? true,
        };

    const merged: InputCalibrationItem[] = [
      fingerCard,
      buildSingleMedicationCalibrationCard(medications),
      { ...eating, kind: "eating-window", showActions: eating.showActions ?? false },
    ];
    return merged;
  })();

  // Weight Management — moved to Home page (post-meal)
  // const activityLogCardsLive: readonly ActivityLogCard[] = [
  //   {
  //     id: "weight",
  //     ...
  //   },
  // ];

  // const familyGroup = useMemo(() => {
  //   const fromMeasure = measureData?.group;
  //   if (fromMeasure?.id && UUID_RE.test(fromMeasure.id)) {
  //     return {
  //       id: fromMeasure.id,
  //       name: fromMeasure.name ?? "Family",
  //       member_count: fromMeasure.member_count ?? 0,
  //     };
  //   }
  //   const first = groupsData?.groups?.[0];
  //   const gid = first?.group_id || first?.id;
  //   if (gid && UUID_RE.test(gid)) {
  //     return {
  //       id: gid,
  //       name: first.name ?? "Family",
  //       member_count: first.member_count ?? 0,
  //     };
  //   }
  //   return null;
  // }, [measureData?.group, groupsData?.groups]);

  // const groupsCount = groupsData?.groups?.length ?? 0;
  // const familyGroupSubtitle =
  //   groupsCount === 0
  //     ? "NO GROUP YET · TAP TO MANAGE"
  //     : groupsCount === 1 && familyGroup
  //       ? `${familyGroup.name.toUpperCase()} • ${familyGroup.member_count} ${
  //           familyGroup.member_count === 1 ? "MEMBER" : "MEMBERS"
  //         }`
  //       : `${groupsCount} GROUPS · TAP TO MANAGE`;

  // const openFamilyGroups = () => setFamilyGroupsSheetOpen(true);

  const { data: goalsResponse, isLoading: goalsLoading } = useQuery({
    queryKey: ["userGoals"],
    queryFn: () => GoalsService.getUserGoals(),
    staleTime: 3 * 60 * 1000,
  });

  const diabetesApiType = goalsResponse?.goals?.diabetes_type ?? "type2";
  const focusTitle = GoalsService.formatDiabetesFocusTitle(diabetesApiType);

  const handleFocusSave = async (newDiabetesApiType: string) => {
    const goals = goalsResponse?.goals;
    if (!goals) {
      return;
    }
    try {
      setFocusSaving(true);
      await GoalsService.updateUserGoals(
        GoalsService.goalsToUpdatePayload(goals, { diabetes_type: newDiabetesApiType }),
      );
      await queryClient.invalidateQueries({ queryKey: ["userGoals"] });
      setFocusSheetOpen(false);
    } catch {
      // silent
    } finally {
      setFocusSaving(false);
    }
  };

  const handleMedicationAdded = async () => {
    await refetchMedications();
    refetchMeasure();
    queryClient.invalidateQueries({ queryKey: ["homePageData"] });
  };

  /** POST /food_scanner/medication/done/ — same as Medication Management “Done” */
  const handleMedicationTaken = async (timingId: string) => {
    if (medicationMarkingId) return;
    const existing = medications.find((m) => m.timing_id === timingId);
    try {
      setMedicationMarkingId(timingId);
      const updated = await medicationService.markMedicationDone(timingId, existing);
      queryClient.setQueryData<LocalMedication[]>(
        ["medications", "measure-inputs"],
        (prev) => (prev ?? []).map((m) => (m.timing_id === timingId ? updated : m)),
      );
      await refetchMedications();
      refetchMeasure();
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      window.dispatchEvent(new CustomEvent("medicationsUpdated"));
    } catch {
      // silent
    } finally {
      setMedicationMarkingId(null);
    }
  };

  const fingerSaveInFlightRef = useRef(false);

  /** Single PUT /food_scanner/glucose-readings/ — never fire twice for one Save. */
  const handleFingerSaved = useCallback(async (payload: FingerPrickSavePayload) => {
    if (fingerSaveInFlightRef.current) return;
    fingerSaveInFlightRef.current = true;

    const useManualTime =
      payload.mode === "past" || Boolean(payload.useManualTime && payload.loggedAt);
    const enteredAt = useManualTime && payload.loggedAt
      ? new Date(payload.loggedAt)
      : new Date();
    const dateStr = `${enteredAt.getFullYear()}-${String(enteredAt.getMonth() + 1).padStart(2, "0")}-${String(enteredAt.getDate()).padStart(2, "0")}`;
    const timeStr = `${String(enteredAt.getHours()).padStart(2, "0")}:${String(enteredAt.getMinutes()).padStart(2, "0")}`;

    try {
      await glucoseReadingsService.saveReadingLog({
        readingType: payload.readingType,
        value: payload.mgDl,
        mealTiming: payload.mealTiming,
        enteredAt: useManualTime ? enteredAt.toISOString() : undefined,
      });
      appendFingerPrickLogHistory({
        mgDl: payload.mgDl,
        date: dateStr,
        time: timeStr,
        readingType: payload.readingType,
        mealTiming: payload.mealTiming,
      });
      setFingerHistoryVersion((v) => v + 1);
      void refetchGlucose();
      void refetchMeasure();
    } finally {
      fingerSaveInFlightRef.current = false;
    }
  }, [refetchGlucose, refetchMeasure]);

  return (
    <>
  <div className="scrollbar-hide relative mx-auto flex h-dvh max-h-dvh w-full min-w-0 max-w-[400px] flex-col overflow-hidden bg-background">
    <div
      className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
      style={{ backgroundImage: `url(${welcomeBg})` }}
    />
    <div className="absolute inset-0 bg-background/80" />

    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-primary/10 via-amber-500/[0.04] to-transparent dark:from-primary/15 dark:via-amber-500/[0.06]"
      aria-hidden
    />

    <div
      className="pointer-events-none absolute inset-0 opacity-35 dark:opacity-25"
      style={{
        backgroundImage:
          "radial-gradient(hsl(var(--primary) / 0.12) 1px, transparent 1px)",
        backgroundSize: "14px 14px",
      }}
      aria-hidden
    />

    <div className="scrollbar-hide relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-20 pt-safe">
      <header className="flex items-center justify-between gap-2 pb-3 sm:gap-3">
        <div className="min-w-0">
          <p className={measurePageSubtitle}>Health metrics</p>
          <h1 className={measurePageTitle}>Measure</h1>
        </div>
        <button
          type="button"
          onClick={() => navigate("/glucera/profilepage")}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-muted"
          aria-label="Profile"
        >
          <User className="h-5 w-5 text-[#737DFE]" />
        </button>
      </header>

      {/* ── Needs attention (shrink-0: flex-col parent se squeeze nahi hone dena; niche scroll zone) ── */}
      {/* <article className="relative flex shrink-0 flex-col overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60 max-md:max-h-[min(560px,_68dvh)]">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-amber-500/[0.1] via-amber-500/[0.03] to-transparent dark:from-amber-500/[0.14]"
          aria-hidden
        />
        Fixed header
        <div className="relative shrink-0 border-b border-border/70 px-4 pb-4 pt-4 sm:px-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-[hsl(var(--warning))]" aria-hidden />
              <p className={measureEyebrowWarning}>Needs attention</p>
            </div>
            <p className={measureEyebrow}>
              {measureLoading
                ? "…"
                : `${attentionItems.length} ${attentionItems.length === 1 ? "item" : "items"}`}
            </p>
          </div>
        </div>

        Scrolls when content taller than viewport / card cap
        <div className="relative scrollbar-hide min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-5 sm:px-5 md:overflow-visible md:pb-6">
          <div className="space-y-5">
            <p className={measureBody}>
              {measureLoading ? (
                "Loading…"
              ) : attentionItems.length > 0 ? (
                <>
                  {attentionItems.length === 1 ? "One update would " : `${attentionItems.length} updates would `}
                  <span className="font-semibold text-foreground">improve</span> your next prediction.
                </>
              ) : (
                "You're up to date — no inputs need attention right now."
              )}
            </p>

            <ul className="space-y-3">
              {measureLoading && (
                <li
                  className="h-[72px] animate-pulse rounded-xl border border-border bg-muted/50"
                  aria-hidden
                />
              )}
              {!measureLoading && attentionItems.length === 0 && (
                <li className="text-sm text-muted-foreground">Nothing needs attention.</li>
              )}
              {!measureLoading &&
                attentionItems.map(({ id, title, subtitle, Icon, stale }) => (
                <li key={id}>
                  <div
                    className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-3.5 text-left shadow-sm"
                    aria-label={`${title}: ${subtitle}`}
                  >
                    <div
                      className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border ${
                        stale
                          ? "border-amber-200/70 bg-amber-50/90 dark:border-amber-900/45 dark:bg-amber-950/40"
                          : "border-sky-200/70 bg-sky-50/90 dark:border-sky-900/45 dark:bg-sky-950/35"
                      }`}
                    >
                      <Icon
                        className={`h-5 w-5 shrink-0 ${stale ? "text-amber-600 dark:text-amber-400" : "text-primary"}`}
                        strokeWidth={1.75}
                        aria-hidden
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={measureCardTitle}>{title}</p>
                      <p className={`mt-1 ${stale ? measureCardMetaWarning : measureCardMeta}`}>
                        {subtitle}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </article> */}

      {/* ── Inputs / Daily calibration ── */}
      <section className="mt-2 shrink-0 space-y-2 pb-1" aria-labelledby="inputs-heading">
        {/* <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-primary font-sans text-caption1 font-bold tabular-nums text-primary-foreground shadow-primary">
              01
            </span>
            <div className="min-w-0">
              <h2 id="inputs-heading" className={measureSectionTitle}>
                Inputs
              </h2>
              <p className={`mt-0.5 ${measureEyebrow}`}>What powers your predictions</p>
            </div>
          </div>
          <p className={`mt-2 shrink-0 ${measureEyebrow}`}>Daily</p>
        </div>

        <div className="flex items-center justify-between gap-3 border-b border-border pb-3">
          <p className={`${measureEyebrow} text-foreground`}>Daily calibration</p>
          <p className={measureEyebrow}>
            {calibrationItems.length} items
          </p>
        </div> */}

        <ul className="space-y-2">
          {calibrationItems.map(
            ({
              id,
              kind,
              timingId,
              title,
              value,
              subtitleCaps,
              Icon,
              accent,
              leftAction,
              rightAction,
              showActions = true,
            }) => {
              const isMedication =
                kind === "medication" ||
                id === "metformin" ||
                id === "medication-primary";
              const medMarking = isMedication && medicationMarkingId === timingId;
              const medLogNowDisabled = isMedication && medMarking;
              // per-card color theme
              const isFingerPrick = kind === "finger-prick";
              const isEatingWindow = id === "eating-window";
              const valueClass =
                accent === "warning" ? "text-amber-600 dark:text-amber-400" : "text-foreground";
              const subtitleClass =
                accent === "warning" ? measureCardMetaWarning : measureCardMeta;
              const iconWrap = isMedication
                ? "border border-amber-200/70 bg-amber-50/90 dark:border-amber-900/45 dark:bg-amber-950/40"
                : isFingerPrick
                ? "border border-blue-200/70 bg-blue-50/90 dark:border-blue-900/45 dark:bg-blue-950/35"
                : isEatingWindow
                ? "border border-green-200/70 bg-green-50/90 dark:border-green-900/45 dark:bg-green-950/35"
                : "border border-sky-200/70 bg-sky-50/90 dark:border-sky-900/45 dark:bg-sky-950/35";
              const iconClass = isMedication
                ? "text-amber-600 dark:text-amber-400"
                : isFingerPrick
                ? "text-blue-600 dark:text-blue-400"
                : isEatingWindow
                ? "text-green-600 dark:text-green-400"
                : "text-primary";
              const showListSubtitle =
                Boolean(subtitleCaps) && !isFingerPrick && !isMedication;
              const isCompactLogCard = isFingerPrick || isMedication;
              const cardGradient = isMedication
                ? "from-amber-500/[0.07] to-transparent"
                : isFingerPrick
                ? "from-blue-500/[0.07] to-transparent"
                : isEatingWindow
                ? "from-green-500/[0.07] to-transparent"
                : "from-primary/[0.06] to-transparent";

              return (
                <li key={id}>
                  <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-sm backdrop-blur-sm dark:border-border/60">
                    <div
                      className={`pointer-events-none absolute inset-x-0 top-0 h-12 bg-gradient-to-b ${cardGradient}`}
                      aria-hidden
                    />
                    {isCompactLogCard ? (
                      <div className="relative flex items-center gap-3 px-4 py-3.5">
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconWrap}`}
                        >
                          <Icon className={`h-[22px] w-[22px] ${iconClass}`} strokeWidth={1.85} aria-hidden />
                        </div>
                        <span className={measureCardHeading}>{title}</span>
                      </div>
                    ) : (
                    <button
                      type="button"
                      className="relative flex w-full items-start gap-3 p-4 text-left outline-none ring-offset-background transition-colors hover:bg-muted/30 focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={() => {
                        if (kind === "finger-prick") openFingerSheet("now");
                        else if (isMedication) openMedicationAddSheet();
                      }}
                    >
                      <div
                        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${iconWrap}`}
                      >
                        <Icon className={`h-[22px] w-[22px] ${iconClass}`} strokeWidth={1.85} aria-hidden />
                      </div>

                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                          <span className={measureCardHeading}>
                            {title}
                          </span>
                          {value ? (
                            <span className={`${measureCardValueSubhead} ${valueClass}`}>
                              {value}
                            </span>
                          ) : null}
                        </div>
                        {showListSubtitle ? (
                          <p className={`mt-1.5 ${subtitleClass}`}>
                            {subtitleCaps}
                          </p>
                        ) : null}
                      </div>
                    </button>
                    )}

                    {showActions && leftAction && rightAction && (
                      <>
                        <div className="border-t border-dashed border-border px-4" aria-hidden />
                        <div className="grid grid-cols-2">
                          <button
                            type="button"
                            disabled={medLogNowDisabled}
                            className={cn(
                              "flex flex-col items-center gap-1.5 outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-50",
                              isCompactLogCard ? "py-3" : "gap-2 py-4",
                              measureActionLabel
                            )}
                            onClick={() => {
                              if (kind === "finger-prick" && leftAction === "LOG NOW") {
                                openFingerSheet("now");
                                return;
                              }
                              if (isMedication) {
                                openMedicationAddSheet();
                              }
                            }}
                          >
                            <Clock className="h-5 w-5 text-muted-foreground" strokeWidth={1.85} aria-hidden />
                            {medMarking ? "…" : leftAction}
                          </button>
                          <button
                            type="button"
                            className={cn(
                              "flex flex-col items-center border-l border-dashed border-border outline-none transition-colors hover:bg-muted/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                              isCompactLogCard ? "gap-1.5 py-3" : "gap-2 py-4",
                              measureActionLabel
                            )}
                            onClick={() => {
                              if (kind === "finger-prick" && rightAction === "History") {
                                setFingerPastSheetOpen(true);
                                return;
                              }
                              if (isMedication) openMedicationPastSheet();
                            }}
                          >
                            <TimerReset className="h-5 w-5 text-muted-foreground" strokeWidth={1.85} aria-hidden />
                            {rightAction}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </li>
              );
            },
          )}
        </ul>
      </section>

      {/* ── Exercise / manual activity log ── */}
      <section className="mt-2 shrink-0 space-y-2 pb-1" aria-labelledby="exercise-heading">
        <div className="mb-1 flex items-baseline justify-between gap-4 px-0.5">
          <span id="exercise-heading" className={measureEyebrow}>
            Activity
          </span>
          <span className={measureEyebrow}>Manual log</span>
        </div>
        <ManualExerciseLogCard
          exercises={todayExercises}
          loading={exercisesLoading}
          onLog={openExerciseLogSheet}
        />
      </section>

      {/* Weight Management — moved to Home page (post-meal) */}

      {/* ── Personalization ── */}
      <section className="mt-2 shrink-0 pb-2" aria-labelledby="personalization-label">
        <div className="mb-2 flex items-baseline justify-between gap-4 px-0.5">
          <span
            id="personalization-label"
            className={measureEyebrow}
          >
            Personalization
          </span>
          <span className={measureEyebrow}>Editable</span>
        </div>

        {/* Your focus */}
        <div className="relative mb-2 overflow-hidden rounded-3xl border border-border/80 bg-gradient-to-br from-card via-card to-[hsl(var(--primary-light))]/40 p-3.5 shadow-sm backdrop-blur-sm dark:border-border/60 dark:to-[hsl(var(--primary-light))]/25">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-primary/[0.08] via-amber-500/[0.04] to-transparent dark:from-primary/[0.12]"
            aria-hidden
          />
          <p className={`relative ${measureEyebrow} text-primary`}>Your focus</p>
          <div className="relative mt-2 flex items-start justify-between gap-4">
            <h3 className={`min-w-0 flex-1 ${measureFocusTitle}`}>
              {goalsLoading ? "Loading…" : focusTitle}
            </h3>
            <button
              type="button"
              className={`shrink-0 rounded-full bg-gradient-primary px-4 py-2 shadow-primary outline-none transition-[transform,opacity] hover:opacity-90 active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 ${measurePrimaryButtonLabel}`}
              disabled={goalsLoading || focusSaving}
              onClick={() => setFocusSheetOpen(true)}
              aria-label="Change your focus"
            >
              Change →
            </button>
          </div>
          <p className={`mt-2 ${measureFocusBody}`}>
            Bite Balance personalizes its voice and emphasis around glucose control and meal protocols.
          </p>
        </div>

        {/* Family group — moved to Home page (post-meal) */}
        {/* <ul className="flex flex-col gap-2">
          {personalizationRows.map(({ id, title, subtitleCaps, Icon }) => {
            const rowSubtitle =
              id === "family-group" ? familyGroupSubtitle || subtitleCaps : subtitleCaps;
            const familyLoading = id === "family-group" && (measureLoading || groupsLoading);
            return (
            <li key={id}>
              <button
                type="button"
                className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card px-4 py-3 text-left shadow-sm outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => {
                  if (id === "family-group") openFamilyGroups();
                }}
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[hsl(var(--info))]/25 bg-[hsl(var(--info-light))]">
                  <Icon className="h-[22px] w-[22px] text-[hsl(var(--info))]" strokeWidth={1.75} aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={`block ${measureCardTitle}`}>{title}</span>
                  <span className={`mt-0.5 block ${measureCardMeta}`}>
                    {familyLoading ? "Loading…" : rowSubtitle}
                  </span>
                </span>
                <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" strokeWidth={2} aria-hidden />
              </button>
            </li>
          );
          })}
        </ul> */}
      </section>

    </div>

    <BottomNavBar activeIndex={2} />
  </div>

  <FingerPrickLogSheet
    open={fingerSheetOpen}
    onClose={() => setFingerSheetOpen(false)}
    initialMode={fingerSheetMode}
    currentReading={
      glucoseData?.fingerPrick
        ? { value: glucoseData.fingerPrick, date: glucoseData.fingerPrickDate, time: glucoseData.fingerPrickTime }
        : null
    }
    onSaved={handleFingerSaved}
  />

  <FingerPrickPastListSheet
    open={fingerPastSheetOpen}
    onClose={() => setFingerPastSheetOpen(false)}
    readings={fingerPastReadings}
    loading={measureLoading}
    dateLabel={measureData?.date_local}
  />

  <MedicationAddSheet
    open={medAddSheetOpen}
    onClose={() => setMedAddSheetOpen(false)}
    initialName=""
    onSaved={() => void handleMedicationAdded()}
  />

  <MedicationPastListSheet
    open={medPastSheetOpen}
    onClose={() => setMedPastSheetOpen(false)}
    medications={medications}
    loading={medicationsLoading}
    markingTimingId={medicationMarkingId}
    onMarkTaken={(tid) => void handleMedicationTaken(tid)}
    onAddMedication={openMedicationAddSheet}
  />

  <DiabetesFocusSheet
    open={focusSheetOpen}
    onClose={() => !focusSaving && setFocusSheetOpen(false)}
    currentApiType={diabetesApiType}
    saving={focusSaving}
    onSave={handleFocusSave}
  />

  <ManualExerciseLogSheet
    open={exerciseLogSheetOpen}
    onClose={() => setExerciseLogSheetOpen(false)}
    onSaved={() => void refetchExercises()}
  />

  {/* Family group — moved to Home page (post-meal) */}
  {/* <FamilyGroupsSheet
    open={familyGroupsSheetOpen}
    onClose={() => setFamilyGroupsSheetOpen(false)}
    onCreateGroup={() => setShowCreateGroupModal(true)}
  />

  <CreateGroupModal
    isOpen={showCreateGroupModal}
    onClose={() => {
      setShowCreateGroupModal(false);
      queryClient.invalidateQueries({ queryKey: ["measurePage"] });
      queryClient.invalidateQueries({ queryKey: ["family", "groups"] });
    }}
  /> */}
  </>
  );
};

export default CustomScreenPage;